import { z } from 'zod';

import {
  createEntityAdapterSchema,
  EntityMetadataSchema,
  findDuplicateIds,
  findDuplicateNames,
  IdSchema,
  ReferenceSchema,
} from './common';

// Handoff-specific error messages
export const HANDOFF_ERRORS = {
  required: 'This field is required.',
  max: (value: number) => `It must be under ${value} characters.`,
  handoff: {
    description: {
      invalidInput:
        'Value should only contain alphanumerics and some punctuation.',
    },
  },
};

// Granular field-level schemas for handoff validation
export const SipHeaderKeySchema = z
  .string()
  .max(100, HANDOFF_ERRORS.max(100))
  .regex(
    /^[A-Za-z0-9!#$%&'*+\-.^_~]*$/,
    HANDOFF_ERRORS.handoff.description.invalidInput,
  );

export const SipHeaderValueSchema = z
  .string()
  .max(1000, HANDOFF_ERRORS.max(1000))
  .regex(/^[\x20-\x7E]*$/, HANDOFF_ERRORS.handoff.description.invalidInput);

export const HandoffNameSchema = z
  .string({
    required_error: HANDOFF_ERRORS.required,
  })
  .trim()
  .min(1, HANDOFF_ERRORS.required)
  .max(100, HANDOFF_ERRORS.max(100));

export const HandoffDescriptionSchema = z
  .string({
    required_error: HANDOFF_ERRORS.required,
  })
  .trim()
  .max(300, HANDOFF_ERRORS.max(300));

export const HandoffPhoneNumberSchema = z
  .string()
  .trim()
  .min(1, HANDOFF_ERRORS.required);

export const OutboundEndpointSchema = z
  .string()
  .trim()
  .min(1, HANDOFF_ERRORS.required);

export const OutboundEncryptionSchema = z.enum(['TLS/SRTP', 'UDP/RTP']);

export const HandoffReferencesSchema = z.object({
  topics: ReferenceSchema,
  flowSteps: ReferenceSchema,
});

export const SipInviteHandoffSchema = z.object({
  $case: z.literal('invite'),
  value: z.object({
    phoneNumber: z.string(),
    outboundEndpoint: z.string(),
    outboundEncryption: z.enum(['TLS/SRTP', 'UDP/RTP']),
  }),
});

export const SipReferHandoffSchema = z.object({
  $case: z.literal('refer'),
  value: z.object({
    phoneNumber: z.string(),
  }),
});

export const SipByeHandoffSchema = z.object({
  $case: z.literal('bye'),
  value: z.object({}),
});

export const SipConfigUnionSchema = z.discriminatedUnion('$case', [
  SipInviteHandoffSchema,
  SipReferHandoffSchema,
  SipByeHandoffSchema,
]);

export const SipConfigSchema = z.object({
  config: SipConfigUnionSchema,
});

export const HandoffSchema = z
  .object({
    id: IdSchema,
    name: HandoffNameSchema,
    description: HandoffDescriptionSchema,
    sipConfig: z.object({
      config: z.discriminatedUnion('$case', [
        z.object({
          $case: z.literal('refer'),
          value: z.object({
            phoneNumber: HandoffPhoneNumberSchema,
          }),
        }),
        z.object({
          $case: z.literal('invite'),
          value: z.object({
            phoneNumber: HandoffPhoneNumberSchema,
            outboundEndpoint: OutboundEndpointSchema,
            outboundEncryption: OutboundEncryptionSchema,
          }),
        }),
        z.object({
          $case: z.literal('bye'),
          value: z.object({}),
        }),
      ]),
    }),
    sipHeaders: z.object({
      headers: z.array(
        z.object({
          key: SipHeaderKeySchema,
          value: SipHeaderValueSchema,
        }),
      ),
    }),
    active: z.boolean(),
    references: HandoffReferencesSchema,
    isDefault: z.boolean(),
  })
  .merge(EntityMetadataSchema);

// Handoffs collection schema
export const HandoffsSchema = z
  .object({
    handoffs: createEntityAdapterSchema(HandoffSchema),
  })
  .superRefine((data, ctx) => {
    const handoffs = Object.values(data.handoffs.entities).filter(
      handoff => handoff.active,
    );
    const duplicateNames = findDuplicateNames(handoffs);
    const duplicateIds = findDuplicateIds(handoffs);
    for (const duplicate of duplicateNames) {
      ctx.addIssue({
        path: ['handoffs', 'entities', duplicate.id, 'name'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate name',
      });
    }
    for (const duplicate of duplicateIds) {
      ctx.addIssue({
        path: ['handoffs', 'entities', duplicate.id, 'id'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate ID',
      });
    }
  });
